import type React from 'react';
import { useEffect, useRef, useState } from 'react';
import useSWR from 'swr';
import { Check, Download, ImageOff, ImagePlus, Loader2, Printer, Sparkles, Trash2 } from 'lucide-react';
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { cn } from '@/lib/utils';
import { extractApiError } from '@/utils/apiError';
import {
  deleteQrBackground, fetchQrBackgroundBlob, generateQrBackground, getQrBackgrounds, putReviewForm, uploadQrBackground,
  type QrBackgroundDto, type QrBackgroundsDto, type QrCardOptions,
} from '@/api/reviews';
import { downloadBlob } from './qr';
import {
  DEFAULT_CARD, PRESET_BGS, TEMPLATES, bgSrcFor, cardBlob, loadImg, renderQrCard, templateOf,
} from './cardRender';
import { PRINT_SHOP_SEARCH } from './QrHowto';

/** Карточка QR для печати (Алексей 22–23.10, #1976 → #2009): «Настроить» — шаблон, фон,
 *  надписи; «Скачать» — печать уже настроенной карточки. Surf-филиалам свои три шаблона. */

const SURF_LOGO = '/brand/surf-coffee.png';

// Фоны лежат под JWT — <img src> их не получит; держим objectURL на сессию.
const bgUrls = new Map<string, Promise<string>>();
function bgObjectUrl(path: string): Promise<string> {
  let p = bgUrls.get(path);
  if (!p) {
    p = fetchQrBackgroundBlob(path).then((b) => URL.createObjectURL(b));
    p.catch(() => bgUrls.delete(path));
    bgUrls.set(path, p);
  }
  return p;
}

const isPreset = (path: string | null) => !!path && PRESET_BGS.includes(path);

/** Готовый src: встроенный — как есть, загруженный — objectURL. */
function useResolvedSrc(path: string | null): string | null {
  const [src, setSrc] = useState<string | null>(null);
  useEffect(() => {
    if (!path || isPreset(path)) { setSrc(null); return; }
    let alive = true;
    void bgObjectUrl(path).then((u) => { if (alive) setSrc(u); }).catch(() => {});
    return () => { alive = false; };
  }, [path]);
  return isPreset(path) ? path : src;
}

function useImage(src: string | null, wanted: boolean) {
  const [st, setSt] = useState<{ src: string | null; img: HTMLImageElement | null } | null>(null);
  useEffect(() => {
    if (!wanted) { setSt({ src: null, img: null }); return; }
    if (!src) return;
    let alive = true;
    void loadImg(src).then((i) => { if (alive) setSt({ src, img: i }); }).catch(() => { if (alive) setSt({ src, img: null }); });
    return () => { alive = false; };
  }, [src, wanted]);
  const ready = !wanted || (st != null && st.src === src);
  return { img: ready ? st?.img ?? null : null, ready };
}

export function useQrBackgrounds(zoneId: string) {
  return useSWR<QrBackgroundsDto>(['qr-backgrounds', zoneId], async () => (await getQrBackgrounds(zoneId)).data, { revalidateOnFocus: false });
}

export function withCardDefaults(card: Partial<QrCardOptions> | undefined, branchName: string): QrCardOptions {
  return { ...DEFAULT_CARD, title: branchName, ...(card ?? {}) };
}

function useCardImages(card: QrCardOptions, list: QrBackgroundDto[] | undefined, surf: boolean) {
  const tpl = templateOf(card.template ?? undefined, surf);
  const path = bgSrcFor(card, tpl, (id) => list?.find((b) => b.id === id)?.url ?? null);
  const bg = useImage(useResolvedSrc(path), !!path);
  const logo = useImage(SURF_LOGO, surf);
  return { bg: bg.img, logo: logo.img, ready: bg.ready && logo.ready };
}

/** Живое превью карточки. */
export function QrCardPreview({ url, logoSrc, card, list, surf, width = 600, className, onRendered }: {
  url: string; logoSrc: string | null; card: QrCardOptions; list: QrBackgroundDto[] | undefined; surf: boolean;
  width?: number; className?: string; onRendered?: (canvas: HTMLCanvasElement) => void;
}) {
  const ref = useRef<HTMLCanvasElement>(null);
  const { bg, logo, ready } = useCardImages(card, list, surf);
  const [drawn, setDrawn] = useState(false);
  useEffect(() => {
    if (!ready || !ref.current) return;
    let alive = true;
    const off = document.createElement('canvas');
    void renderQrCard(off, { url, logoSrc, card, bg, surfLogo: logo }, width, surf).then(() => {
      const c = ref.current;
      if (!alive || !c) return;
      c.width = off.width;
      c.height = off.height;
      c.getContext('2d')!.drawImage(off, 0, 0);
      setDrawn(true);
      onRendered?.(c);
    });
    return () => { alive = false; };
  }, [ready, bg, logo, url, logoSrc, card.template, card.title, card.legend, card.tagline, card.frame_opacity, card.bg_id, width, surf]);
  return (
    // self-start: в flex-ряду превью иначе тянется на высоту соседней колонки (#1990).
    <div className={cn('relative self-start overflow-hidden rounded-md bg-white shadow-sm ring-1 ring-black/10', className)} style={{ aspectRatio: '420 / 592' }}>
      <canvas ref={ref} className={cn('block h-auto w-full', !drawn && 'invisible')} />
      {!drawn && <Loader2 className="absolute inset-0 m-auto size-5 animate-spin text-gray-400" />}
    </div>
  );
}

function BgThumb({ path, selected, onClick, onDelete, children }: {
  path: string | null; selected: boolean; onClick: () => void; onDelete?: () => void; children?: React.ReactNode;
}) {
  const src = useResolvedSrc(path);
  return (
    <div className="group relative">
      <button
        type="button"
        onClick={onClick}
        className={cn(
          'flex h-16 w-11 items-center justify-center overflow-hidden rounded-md border-2 bg-muted transition-colors',
          selected ? 'border-primary' : 'border-transparent hover:border-border',
        )}
      >
        {path ? (src ? <img src={src} alt="" className="size-full object-cover" /> : <Loader2 className="size-4 animate-spin text-muted-foreground" />) : children}
      </button>
      {selected && <Check className="absolute right-0.5 top-0.5 size-4 rounded-full bg-primary p-0.5 text-primary-foreground" />}
      {onDelete && (
        <button
          type="button"
          onClick={onDelete}
          className="absolute bottom-1 right-1 hidden rounded bg-black/60 p-0.5 text-white group-hover:block"
          aria-label="Удалить фон"
        >
          <Trash2 className="size-3.5" />
        </button>
      )}
    </div>
  );
}

const sectionTitle = 'text-xs font-medium uppercase tracking-wider text-muted-foreground';

export function QrCardDesigner({ open, onOpenChange, zoneId, url, logoSrc, initial, surf, onSaved }: {
  open: boolean; onOpenChange: (v: boolean) => void; zoneId: string; url: string; logoSrc: string | null;
  initial: QrCardOptions; surf: boolean; onSaved: () => Promise<void>;
}) {
  const { data, mutate } = useQrBackgrounds(zoneId);
  const [card, setCard] = useState<QrCardOptions>(initial);
  const [wish, setWish] = useState('');
  const [busy, setBusy] = useState<'upload' | 'generate' | 'save' | null>(null);
  const [error, setError] = useState<string | null>(null);
  const fileRef = useRef<HTMLInputElement>(null);
  useEffect(() => { if (open) { setCard(initial); setError(null); } }, [open]);

  const set = (p: Partial<QrCardOptions>) => setCard((c) => ({ ...c, ...p }));
  const tpl = templateOf(card.template ?? undefined, surf);
  const effectiveBg = card.bg_id ?? tpl.bg ?? 0;

  async function run(kind: 'upload' | 'generate', fn: () => Promise<{ data: QrBackgroundsDto }>) {
    setBusy(kind);
    setError(null);
    try {
      const r = await fn();
      await mutate(r.data, { revalidate: false });
      if (r.data.created) set({ bg_id: r.data.created.id });
    } catch (e) {
      setError(extractApiError(e, 'Не получилось'));
    } finally {
      setBusy(null);
    }
  }

  async function remove(id: number) {
    try {
      const r = await deleteQrBackground(zoneId, id);
      await mutate(r.data, { revalidate: false });
      if (card.bg_id === id) set({ bg_id: null });
    } catch (e) {
      setError(extractApiError(e, 'Не удалось удалить'));
    }
  }

  async function save() {
    setBusy('save');
    setError(null);
    try {
      await putReviewForm(zoneId, { options: { qr_card: card } });
      await onSaved();
      onOpenChange(false);
    } catch (e) {
      setError(extractApiError(e, 'Не удалось сохранить'));
    } finally {
      setBusy(null);
    }
  }

  const left = data?.generations_left ?? 0;
  return (
    <Dialog open={open} onOpenChange={onOpenChange} widthClassName="sm:max-w-5xl">
      <DialogContent className="max-h-[calc(100dvh-2rem)] overflow-y-auto sm:max-w-5xl">
        <DialogHeader><DialogTitle>Карточка с QR-кодом</DialogTitle></DialogHeader>
        <div className="flex flex-col gap-6 sm:flex-row">
          <div className="min-w-0 flex-1 space-y-4 text-sm">
            <div className="divide-y divide-border rounded-xl border border-border">
              <div className="space-y-2 p-3">
                <div className={sectionTitle}>Стиль</div>
                <div className="flex flex-wrap gap-3">
                  {TEMPLATES.filter((t) => t.surf === surf).map((t) => (
                    <button
                      key={t.key}
                      type="button"
                      onClick={() => set({ template: t.key, bg_id: null, tagline: null })}
                      className={cn('flex flex-col items-center gap-1 rounded-lg border-2 p-1 transition-colors',
                        tpl.key === t.key ? 'border-primary' : 'border-transparent hover:border-border')}
                    >
                      <QrCardPreview
                        url={url} logoSrc={null} surf={surf} list={data?.backgrounds} width={210} className="w-20"
                        card={{ ...card, template: t.key, bg_id: null, tagline: null }}
                      />
                      <span className="text-xs">{t.label}</span>
                    </button>
                  ))}
                </div>
              </div>

              {tpl.bg != null || card.bg_id ? (
                <div className="space-y-2 p-3">
                  <div className={sectionTitle}>Фото</div>
                  <div className="flex flex-wrap gap-2">
                    {PRESET_BGS.map((src, i) => (
                      <BgThumb key={src} path={src} selected={effectiveBg === -(i + 1)} onClick={() => set({ bg_id: -(i + 1) })} />
                    ))}
                    {data?.backgrounds.map((b) => (
                      <BgThumb key={b.id} path={b.url} selected={effectiveBg === b.id} onClick={() => set({ bg_id: b.id })} onDelete={() => void remove(b.id)} />
                    ))}
                    <BgThumb path={null} selected={effectiveBg === 0} onClick={() => set({ bg_id: 0 })}>
                      <span className="flex flex-col items-center gap-1 text-[10px] leading-tight text-muted-foreground"><ImageOff className="size-4" />без</span>
                    </BgThumb>
                  </div>
                  <input
                    ref={fileRef}
                    type="file"
                    accept="image/png,image/jpeg,image/webp"
                    className="hidden"
                    onChange={(e) => {
                      const f = e.target.files?.[0];
                      e.target.value = '';
                      if (f) void run('upload', () => uploadQrBackground(zoneId, f));
                    }}
                  />
                  <div className="flex flex-wrap items-center gap-2 pt-1">
                    <Button size="sm" variant="outline" onClick={() => fileRef.current?.click()} disabled={busy !== null}>
                      {busy === 'upload' ? <Loader2 className="size-3.5 animate-spin" /> : <ImagePlus className="size-3.5" />} Своё фото
                    </Button>
                    <Input
                      value={wish}
                      onChange={(e) => setWish(e.target.value.slice(0, 300))}
                      placeholder="Что на фото, например «круассаны и капучино»"
                      className="h-8 min-w-0 flex-1 text-sm"
                      disabled={busy !== null}
                    />
                    <Button size="sm" variant="outline" onClick={() => void run('generate', () => generateQrBackground(zoneId, wish))} disabled={busy !== null || left === 0}>
                      {busy === 'generate' ? <Loader2 className="size-3.5 animate-spin" /> : <Sparkles className="size-3.5" />} Сгенерировать
                    </Button>
                  </div>
                  <p className="text-xs text-muted-foreground">
                    {busy === 'generate' ? 'Рисуем фото — до минуты…' : left > 0 ? `Сегодня можно сгенерировать ещё ${left}` : 'Генерации на сегодня закончились — загрузите своё фото или выберите готовое'}
                  </p>
                </div>
              ) : null}

              <div className="grid gap-2 p-3">
                <div className={sectionTitle}>Заголовок</div>
                <Input value={card.legend} onChange={(e) => set({ legend: e.target.value.slice(0, 120) })} placeholder="Обратная связь управляющему" className="text-sm" />
                <div className={cn(sectionTitle, 'pt-1')}>Филиал</div>
                <Input value={card.title} onChange={(e) => set({ title: e.target.value.slice(0, 80) })} placeholder="Например, Pitstop" className="text-sm" />
                <div className={cn(sectionTitle, 'pt-1')}>Подпись внизу</div>
                <Input value={card.tagline ?? tpl.tagline} onChange={(e) => set({ tagline: e.target.value.slice(0, 160) })} className="text-sm" />
                {tpl.frame && (
                  <label className="flex items-center gap-3 pt-1">
                    <span className="shrink-0 text-muted-foreground">Рамка вокруг кода</span>
                    <input
                      type="range" min={0.1} max={1} step={0.05}
                      value={card.frame_opacity}
                      onChange={(e) => set({ frame_opacity: Number(e.target.value) })}
                      className="min-w-0 flex-1 accent-[var(--color-primary)]"
                    />
                  </label>
                )}
              </div>
            </div>
            {error && <p className="text-sm text-destructive">{error}</p>}
          </div>
          <div className="flex shrink-0 items-start justify-center">
            <QrCardPreview url={url} logoSrc={logoSrc} card={card} list={data?.backgrounds} surf={surf} width={640} className="w-72" />
          </div>
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)}>Отмена</Button>
          <Button onClick={() => void save()} disabled={busy !== null}>
            {busy === 'save' && <Loader2 className="size-4 animate-spin" />} Сохранить
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ── Печать ────────────────────────────────────────────────────────────────────

type PrintSize = 'a6' | 'a5' | 'a4';
const PRINT_SIZES: { key: PrintSize; label: string; hint: string; page: string; w: number; h: number; cols: number; rows: number }[] = [
  { key: 'a6', label: 'A6', hint: '10,5 × 14,8 см · 4 на листе', page: 'A4 portrait', w: 105, h: 148, cols: 2, rows: 2 },
  { key: 'a5', label: 'A5', hint: '14,8 × 21 см · 2 на листе', page: 'A4 landscape', w: 148, h: 210, cols: 2, rows: 1 },
  { key: 'a4', label: 'A4', hint: '21 × 29,7 см · 1 на листе', page: 'A4 portrait', w: 210, h: 296, cols: 1, rows: 1 },
];

/** back — вторая страница с оборотом (#2017). Сетка по центру листа и все ячейки одинаковые,
 *  поэтому при перевороте по длинному краю оборот ложится ровно под лицо без зеркалирования. */
function printHtml(src: string, back: string | null, s: (typeof PRINT_SIZES)[number]) {
  const grid = (u: string) => `<div class="g">${`<div class="c"><img src="${u}" alt=""></div>`.repeat(s.cols * s.rows)}</div>`;
  return '<!doctype html><html><head><meta charset="utf-8">' +
    `<style>@page{size:${s.page};margin:0}html,body{margin:0;padding:0}` +
    `.g{display:grid;grid-template-columns:repeat(${s.cols},${s.w}mm);grid-auto-rows:${s.h}mm;justify-content:center}` +
    '.g+.g{break-before:page}' +
    '.c{box-sizing:border-box;outline:0.2mm dashed #c4c8cf;outline-offset:-0.1mm;break-inside:avoid}' +
    'img{width:100%;height:100%;object-fit:cover;display:block}' +
    '</style></head><body>' +
    grid(src) + (back ? grid(back) : '') + '</body></html>';
}

function printViaIframe(html: string): Promise<void> {
  return new Promise((resolve) => {
    const iframe = document.createElement('iframe');
    iframe.setAttribute('aria-hidden', 'true');
    Object.assign(iframe.style, { position: 'fixed', right: '0', bottom: '0', width: '0', height: '0', border: '0' });
    const cleanup = () => { window.setTimeout(() => iframe.remove(), 1000); };
    iframe.onload = async () => {
      const win = iframe.contentWindow;
      const doc = iframe.contentDocument;
      if (!win || !doc) { cleanup(); resolve(); return; }
      await Promise.all([...doc.images].map((img) => img.decode().catch(() => undefined)));
      win.addEventListener('afterprint', cleanup, { once: true });
      win.focus();
      win.print();
      window.setTimeout(cleanup, 60_000);
      resolve();
    };
    iframe.srcdoc = html;
    document.body.appendChild(iframe);
  });
}

export function QrCardPrintDialog({ open, onOpenChange, zoneId, url, logoSrc, card, surf, onConfigure }: {
  open: boolean; onOpenChange: (v: boolean) => void; zoneId: string; url: string; logoSrc: string | null;
  card: QrCardOptions; surf: boolean; onConfigure?: () => void;
}) {
  const { data } = useQrBackgrounds(zoneId);
  const [size, setSize] = useState<PrintSize>('a6');
  const [busy, setBusy] = useState<'print' | 'download' | null>(null);
  // Двусторонняя печать (#2017): оборот — такая же карточка или другой стиль своего набора.
  const [duplex, setDuplex] = useState(false);
  const [backKey, setBackKey] = useState<string>('same');
  const backCard: QrCardOptions = backKey === 'same' ? card : { ...card, template: backKey as QrCardOptions['template'], bg_id: null, tagline: null };
  const front = useCardImages(card, data?.backgrounds, surf);
  const backImgs = useCardImages(backCard, data?.backgrounds, surf);
  const ready = front.ready && (!duplex || backImgs.ready);
  const spec = PRINT_SIZES.find((s) => s.key === size)!;

  // 300 dpi по ширине выбранного формата.
  async function render(side: 'front' | 'back'): Promise<Blob> {
    const c = document.createElement('canvas');
    const im = side === 'front' ? front : backImgs;
    await renderQrCard(c, { url, logoSrc, card: side === 'front' ? card : backCard, bg: im.bg, surfLogo: im.logo },
      Math.round((spec.w / 25.4) * 300), surf);
    return cardBlob(c);
  }
  const fileBase = (card.title || 'qr').replace(/[\\/:*?"<>|]+/g, ' ').trim();

  async function print() {
    setBusy('print');
    try {
      const u = URL.createObjectURL(await render('front'));
      const b = duplex ? URL.createObjectURL(await render('back')) : null;
      await printViaIframe(printHtml(u, b, spec));
      window.setTimeout(() => { URL.revokeObjectURL(u); if (b) URL.revokeObjectURL(b); }, 90_000);
    } finally { setBusy(null); }
  }
  async function download() {
    setBusy('download');
    try {
      downloadBlob(await render('front'), `${fileBase} — карточка ${spec.label}${duplex ? ' (лицо)' : ''}.png`);
      if (duplex) downloadBlob(await render('back'), `${fileBase} — карточка ${spec.label} (оборот).png`);
    } finally { setBusy(null); }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange} widthClassName="sm:max-w-2xl">
      <DialogContent className="max-h-[calc(100dvh-2rem)] overflow-y-auto sm:max-w-2xl">
        <DialogHeader><DialogTitle>Печать карточки</DialogTitle></DialogHeader>
        <div className="flex flex-col gap-6 sm:flex-row">
          <div className="min-w-0 flex-1 space-y-3 text-sm">
            <div className={sectionTitle}>Размер</div>
            <div className="space-y-1.5">
              {PRINT_SIZES.map((s) => (
                <button
                  key={s.key}
                  type="button"
                  onClick={() => setSize(s.key)}
                  className={cn(
                    'flex w-full items-baseline gap-3 rounded-lg border px-3 py-2 text-left transition-colors',
                    size === s.key ? 'border-primary bg-primary/5' : 'border-border hover:bg-muted',
                  )}
                >
                  <span className="w-8 font-semibold">{s.label}</span>
                  <span className="text-muted-foreground">{s.hint}</span>
                </button>
              ))}
            </div>
            <label className="flex cursor-pointer items-center gap-2 pt-1">
              <input type="checkbox" checked={duplex} onChange={(e) => setDuplex(e.target.checked)} className="size-4 accent-[var(--color-primary)]" />
              <span className="font-medium">Двусторонняя</span>
            </label>
            {duplex && (
              <div className="space-y-1.5">
                <div className="flex flex-wrap gap-1.5">
                  {[{ key: 'same', label: 'Оборот такой же' }, ...TEMPLATES.filter((t) => t.surf === surf && t.key !== templateOf(card.template ?? undefined, surf).key)
                    .map((t) => ({ key: t.key as string, label: t.label }))].map((o) => (
                    <button
                      key={o.key}
                      type="button"
                      onClick={() => setBackKey(o.key)}
                      className={cn('rounded-full border px-2.5 py-1 text-xs transition-colors',
                        backKey === o.key ? 'border-primary bg-primary/5' : 'border-border hover:bg-muted')}
                    >
                      {o.label}
                    </button>
                  ))}
                </div>
                <p className="text-xs text-muted-foreground">Второй страницей идёт оборот. В настройках принтера — двусторонняя, переворот по длинному краю.</p>
              </div>
            )}
            <p className="text-xs leading-relaxed text-muted-foreground">
              A6 входит в акриловую рамку для стола. Печатать можно на обычном принтере или{' '}
              <a href={PRINT_SHOP_SEARCH} target="_blank" rel="noreferrer" className="text-primary hover:underline">в типографии рядом</a>.
            </p>
            {onConfigure && (
              <Button size="sm" variant="ghost" className="-ml-2" onClick={() => { onOpenChange(false); onConfigure(); }}>
                Изменить карточку
              </Button>
            )}
          </div>
          <div className="flex shrink-0 items-start justify-center gap-3">
            <QrCardPreview url={url} logoSrc={logoSrc} card={card} list={data?.backgrounds} surf={surf} width={520} className={duplex ? 'w-36' : 'w-52'} />
            {duplex && <QrCardPreview url={url} logoSrc={logoSrc} card={backCard} list={data?.backgrounds} surf={surf} width={520} className="w-36" />}
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Закрыть</Button>
          <Button variant="outline" onClick={() => void download()} disabled={!ready || busy !== null}>
            {busy === 'download' ? <Loader2 className="size-4 animate-spin" /> : <Download className="size-4" />} Скачать PNG
          </Button>
          <Button onClick={() => void print()} disabled={!ready || busy !== null}>
            {busy === 'print' ? <Loader2 className="size-4 animate-spin" /> : <Printer className="size-4" />} Печать
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
